import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export interface StatusHistoryEntry {
  id: string;
  project_id: string;
  previous_status: string | null;
  new_status: string;
  changed_by: string;
  changed_at: string;
  notes: string | null;
  changedByName?: string;
}

export const useProjectStatusHistory = (projectId: string) => {
  return useQuery({
    queryKey: ['projectStatusHistory', projectId],
    queryFn: async () => {
      // Fetch status history
      const { data: history, error } = await supabase
        .from('project_status_history')
        .select('*')
        .eq('project_id', projectId)
        .order('changed_at', { ascending: false });

      if (error) throw error;
      if (!history || history.length === 0) return [];

      // Get unique user IDs
      const userIds = [...new Set(history.map(h => h.changed_by))];
      
      // Fetch profiles for those users
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .in('id', userIds);

      // Create a map of user ID to name
      const profileMap = new Map(
        (profiles || []).map(p => [
          p.id, 
          `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Unknown User'
        ])
      );

      // Enrich history with names
      return history.map(entry => ({
        ...entry,
        changedByName: profileMap.get(entry.changed_by) || 'Unknown User'
      })) as StatusHistoryEntry[];
    },
    enabled: !!projectId,
  });
};
